import { useEffect, useState } from 'react';
import { Button, Card, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { describeSteps, progress } from '../setup/setupSteps';

const hiddenKey = (orgId) => `ibigdo.setup.hidden.${orgId}`;
const readHidden = (orgId) => {
  try { return localStorage.getItem(hiddenKey(orgId)) === '1'; } catch (e) { return false; }
};
const writeHidden = (orgId, hidden) => {
  try { if (hidden) localStorage.setItem(hiddenKey(orgId), '1'); else localStorage.removeItem(hiddenKey(orgId)); } catch (e) { /* it only comes back next time */ }
};

/**
 * The first-week checklist on the Dashboard for an Org Admin of a new organization: each step ticks itself from what the organization
 * has, and the card goes away once the required steps are done. The server sends steps only to an Org Admin; the wording is in
 * setup/setupSteps.js. "Hide" is remembered in this browser, and a small link brings it back.
 */
function SetupChecklist() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(() => readHidden(orgId));

  useEffect(() => {
    if (!user) return undefined;
    setHidden(readHidden(user.organizationId));
    let cancelled = false;
    axios.get('/api/setup-checklist')
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [user]);

  const steps = describeSteps(data?.steps);
  if (!steps.length || data?.complete) return null;
  const { done, total } = progress(steps);

  const setHide = (value) => { writeHidden(orgId, value); setHidden(value); };
  if (hidden) {
    return (
      <div className="text-end mb-3">
        <Button variant="link" size="sm" className="text-muted p-0" onClick={() => setHide(false)}>Show the set-up steps</Button>
      </div>
    );
  }

  const required = steps.filter((s) => !s.optional);
  const optional = steps.filter((s) => s.optional);
  const row = (s) => (
    <div key={s.key} className="d-flex align-items-start gap-2 py-2 border-top">
      <i className={`bi ${s.done ? 'bi-check-circle-fill text-success' : 'bi-circle text-muted'} mt-1`} />
      <div className="flex-grow-1">
        <div className={s.done ? 'text-muted text-decoration-line-through' : 'fw-semibold'}>{s.title}</div>
        {!s.done && <div className="small text-muted">{s.hint}</div>}
      </div>
      {!s.done && <Link to={s.to} className="btn btn-sm btn-outline-primary text-nowrap">{s.label}</Link>}
    </div>
  );

  return (
    <Card className="mb-4 border-primary">
      <Card.Body>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <h6 className="mb-0"><i className="bi bi-rocket-takeoff me-2" />Get started with iBigDo</h6>
          <span className="text-muted small">{done} of {total} done</span>
          <Link to="/help/walkthroughs?story=set-up-your-items" className="small ms-auto">Watch how to set up your items</Link>
          <Button variant="link" size="sm" className="text-muted p-0" onClick={() => setHide(true)}>Hide</Button>
        </div>
        <ProgressBar now={total ? (done / total) * 100 : 0} style={{ height: '0.4rem' }} className="mb-2" />
        {required.map(row)}
        {optional.length > 0 && <div className="small text-muted pt-2">When you are ready</div>}
        {optional.map(row)}
      </Card.Body>
    </Card>
  );
}

export default SetupChecklist;
